/**
 * CloudSwitchProgressDialog — the steps run by turning on "Upgraded to 5.0" (save, the six
 * migrations, the local server restart), each with its status as it runs.
 *
 * Props:
 *   steps    — CLOUD_SWITCH_STEPS ([{ key, label }])
 *   status   — { [key]: { status: 'pending'|'running'|'done'|'skipped'|'failed', detail } }
 *   running  — true while a run is in progress (close + retry locked)
 *   onRetry  — re-run the failed steps (shown only when something failed)
 *   onClose  — close the dialog
 */

const GLYPH = {
  pending: { char: '○', color: 'var(--color-text-tertiary)', label: 'Waiting' },
  running: { char: '…', color: 'var(--color-primary)', label: 'Running' },
  done: { char: '✓', color: 'var(--color-success)', label: 'Done' },
  skipped: { char: '–', color: 'var(--color-text-tertiary)', label: 'Skipped' },
  failed: { char: '✕', color: 'var(--color-danger)', label: 'Failed' },
};

const CloudSwitchProgressDialog = ({ steps, status, running, onRetry, onClose }) => {
  const failed = steps.filter((s) => status[s.key]?.status === 'failed');
  const saveFailed = status.save?.status === 'failed';
  const finished = !running && steps.every((s) => status[s.key] && status[s.key].status !== 'pending');

  return (
    <div className="pp-dialog-backdrop" onClick={() => { if (!running) onClose(); }}>
      <div className="pp-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Upgrading to 5.0">
        <div className="pp-dialog__header">
          <h2 className="pp-dialog__title">Upgrading to 5.0</h2>
          <button className="dp-drawer__close" onClick={onClose} disabled={running}>{'✕'}</button>
        </div>
        <div className="pp-dialog__body">
          {running ? (
            <p style={{ margin: '0 0 var(--space-3)', lineHeight: 1.6 }}>Keep this page open until every step has finished.</p>
          ) : null}
          <ol style={{ listStyle: 'none', margin: 0, padding: 0 }} aria-live="polite">
            {steps.map((s) => {
              const st = status[s.key] || { status: 'pending', detail: '' };
              const g = GLYPH[st.status] || GLYPH.pending;
              return (
                <li key={s.key} style={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-1) 0', lineHeight: 1.5 }}>
                  <span aria-label={g.label} title={g.label} style={{ width: '1.25rem', flexShrink: 0, textAlign: 'center', fontWeight: 600, color: g.color }}>
                    {g.char}
                  </span>
                  <span>
                    <span style={{ fontWeight: st.status === 'running' ? 600 : 400 }}>{s.label}</span>
                    {st.detail ? (
                      <span style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: st.status === 'failed' ? 'var(--color-danger)' : 'var(--color-text-secondary)' }}>
                        {st.detail}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ol>
          {finished && failed.length ? (
            <p style={{ margin: 'var(--space-3) 0 0', lineHeight: 1.6 }}>
              {saveFailed
                ? 'Nothing was migrated and the local server was not restarted. Retry runs every step again.'
                : 'Retry runs only the failed steps. Anything already migrated is not added again. If a retried step migrates something, the local server restarts again.'}
            </p>
          ) : null}
        </div>
        <div className="pp-dialog__footer">
          {finished && failed.length ? (
            <button className="dp-btn dp-btn--primary" onClick={onRetry}>
              Retry Failed Steps
            </button>
          ) : null}
          <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={running}>
            {running ? 'Running...' : 'Close'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CloudSwitchProgressDialog;
